import { useState } from "react"
import { Link, Navigate, useSearchParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { ArrowRight, Loader2 } from "lucide-react"
import { usePostHog } from "@posthog/react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AuthLayout } from "@/components/auth-layout"
import {
  AuthCard,
  AuthHeading,
  LogoMark,
} from "@/components/auth-card"
import { AuthField, AuthErrorBanner } from "@/components/auth-field"
import { AuthSuccess } from "@/components/auth-success"
import { AuthMaintenance } from "@/components/auth-maintenance"
import { useServerInfo } from "@/hooks/use-server-info"
import { useSession } from "@/hooks/use-session"

const schema = z.object({
  name: z
    .string()
    .min(1, "What should we call you?")
    .max(100, "That name is a bit long"),
  email: z.string().email("That doesn't look like a valid email"),
})

type FormValues = z.infer<typeof schema>

export function SignupPage() {
  const { info, loading } = useServerInfo()
  const session = useSession()
  // Wait until we know whether maintenance is on before mounting the form.
  // Rendering <SignupForm /> too early then unmounting it for <AuthMaintenance />
  // would change the hook count between renders and trip React error #300.
  if (loading || session.status === "loading") return null
  // Already signed in: no reason to show the signup form.
  if (session.status === "authenticated") return <Navigate to="/" replace />
  if (info?.maintenance) return <AuthMaintenance />
  return <SignupForm />
}

function SignupForm() {
  const posthog = usePostHog()
  const [params] = useSearchParams()
  const [submitted, setSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState("")
  const [existingUser, setExistingUser] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: params.get("email") ?? "" },
  })

  const onSubmit = async (values: FormValues) => {
    setFormError(null)
    try {
      const res = await fetch("/api/v1/saas/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(values),
      })
      const body = (await res.json()) as {
        ok?: boolean
        is_existing_user?: boolean
        error?: string
      }

      if (!res.ok) {
        setFormError(humanizeSignupError(res.status, body.error))
        return
      }
      setSubmittedEmail(values.email)
      if (body.is_existing_user) {
        setExistingUser(true)
        return
      }
      posthog?.capture("signup_requested")
      setSubmitted(true)
    } catch {
      setFormError("Couldn't reach the server. Try again in a moment.")
    }
  }

  const handleResend = () => {
    setSubmitted(false)
    setFormError(null)
    form.reset({ name: form.getValues("name"), email: submittedEmail })
  }

  if (existingUser) {
    return (
      <AuthLayout>
        <LogoMark />
        <AuthCard>
          <div className="space-y-6">
            <AuthHeading
              eyebrow="Already registered"
              title="You already have an account"
              description={`${submittedEmail} is already registered. We've emailed a sign-in link so you can jump straight back in.`}
            />
            <Link
              to="/login"
              className="group flex w-full items-center justify-center gap-2 rounded-md bg-rose-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-500/20 transition-all hover:bg-rose-500 hover:shadow-rose-500/30"
            >
              Go to sign in
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </AuthCard>
      </AuthLayout>
    )
  }

  if (submitted) {
    return (
      <AuthLayout>
        <LogoMark />
        <AuthCard>
          <AuthSuccess
            email={submittedEmail}
            purpose="signup"
            onResend={handleResend}
          />
        </AuthCard>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <LogoMark />
      <AuthCard>
        <div className="space-y-7">
          <AuthHeading
            eyebrow="Start your trial"
            title="Create your workspace"
            description="Seven days of full Hivehook, on us. No credit card required, no quota games."
          />

          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <AuthField
              label="Your name"
              error={form.formState.errors.name?.message}
            >
              <Input
                {...form.register("name")}
                placeholder="Alice"
                autoComplete="name"
                autoFocus
                aria-invalid={!!form.formState.errors.name}
              />
            </AuthField>

            <AuthField
              label="Work email"
              hint="Personal works too"
              error={form.formState.errors.email?.message}
            >
              <Input
                {...form.register("email")}
                type="email"
                placeholder="alice@acme.com"
                autoComplete="email"
                aria-invalid={!!form.formState.errors.email}
              />
            </AuthField>

            {formError && <AuthErrorBanner message={formError} />}

            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="group w-full bg-rose-600 text-white shadow-lg shadow-rose-500/20 transition-all hover:bg-rose-500 hover:shadow-rose-500/30 disabled:bg-rose-600/60"
            >
              {form.formState.isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending link
                </>
              ) : (
                <>
                  Send sign-in link
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>

          <div className="border-t border-border/40 pt-5 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-medium text-foreground underline decoration-rose-400/40 decoration-2 underline-offset-4 transition-colors hover:text-rose-400 hover:decoration-rose-400"
            >
              Sign in
            </Link>
          </div>
        </div>
      </AuthCard>

      <p className="mt-6 text-center text-2xs uppercase tracking-[0.18em] text-muted-foreground/50">
        7-day trial, no card required
      </p>
    </AuthLayout>
  )
}

function humanizeSignupError(status: number, raw?: string): string {
  switch (status) {
    case 400:
      return raw || "Something about that email looked off. Try again."
    case 429:
      return "Too many sign-in attempts for that email. Wait a minute and try again."
    case 503:
      return "Email isn't configured on this server. Reach out so we can sort it out."
    default:
      return raw || "Something went wrong. Try again in a moment."
  }
}
